import React, { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router';
import Header from './components/header';
import Footer from './components/footer';
import TermsOfUse from './pages/terms-of-use/TermsOfUse';
import VirtualAgent from './components/VirtualAgent';
import ApplicationV2 from './components/application/applicationV2';
import { DEFAULT_PAGE_SIZE, initialAppCriteria, initialHeaderLinks } from './shared/consts';
import { useAppConfig } from './context/app-configs-context';
import { AppActions, AppCriteria, AppResultInfo, CriteriaConstraint, CustomLink, PageInfo } from './types/decisions';
import { rankApps, rerankApps, rerankManyApps } from './api/decisions.api';
import { getCriteriaValueByType, transformCriteriaData } from './utils';
import { useAuth } from './context/auth-context';
import SessionTokenIframe from './components/SessionTokenIframe/SessionTokenIframe';
import Loading from './components/loading/Loading';
import CardiologyEnhancedV2 from './pages/cardiology/CardiologyEnhancedV2';
import Forms from './pages/forms/Forms';
import Mashups from './pages/mashups/Mashups';
import UnauthorizedBoundary from './components/UnauthorizedBoundary/UnauthorizedBoundary';
import Applications from './components/applications';
import Flows from './factory-components/flows/Flows';
import { useError } from './context/error-context';
import ErrorPage from './pages/errorPage/errorPage';
import CreateWebsite from './components/CreateWebsite/CreateWebsite';
import WebsiteEditor from './pages/websiteEditor/WebsiteEditor';
import './app.scss';

function App() {
    const { isAuthLoading, isAuthenticated } = useAuth();
    const {
        appConfigs: {
            show: {
                headerMenu,
                menuItems,
                header,
                footer,
            },
        },
    } = useAppConfig();
    const [ headerLinks, setHeaderLinks ] = useState<CustomLink[]>(initialHeaderLinks);
    const [ externalItems, setExternalItems ] = useState<CustomLink[]>([]);

    const [ resultLoading, setResultLoading ] = useState<boolean>(true);
    const initialCriteriaData = useRef<AppCriteria>(initialAppCriteria);
    const [ criteriaData, setCriteriaData ] = useState<AppCriteria>(initialAppCriteria);
    const [ criteriaConstraints, setCriteriaConstraints ] = useState<{
        [key: string]: CriteriaConstraint[]
    }>({});
    const [ results, setResults ] = useState<AppResultInfo[]>([]);
    const [ rankMode, setRankMode ] = useState<'regular' | 'multi'>('regular');
    const [ selectedProductIds, setSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ oldSelectedProductIds, setOldSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ showSelectedItems, setShowSelectedItems ] = useState(true);
    const [ pageInfo, setPageInfo ] = useState<PageInfo>({
        page_size: DEFAULT_PAGE_SIZE,
        current_page: 1,
        start: 0,
        end: 0,
        total: 0,
        total_pages: 1,
    });
    const { error, clearError, showError } = useError();

    const setSelectedProductIdsWrapper = (value: SetStateAction<string[]>) => {
        setSelectedProductIds(value);
        setShowSelectedItems(true);
    };

    const onCriteriaApply = useCallback((dataset: string, options = { scrollToTop: true }) => {
        if (options.scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }

        setResultLoading(true);
        rankApps(
            transformCriteriaData(criteriaData, criteriaConstraints),
            pageInfo.page_size,
            1,
            dataset,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, showError ]);

    const onRerank = useCallback((rerankProductId: string, dataset: string, scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankApps(
            transformCriteriaData(criteriaData, criteriaConstraints),
            rerankProductId,
            pageInfo.page_size,
            1,
            dataset,
        ).then(response => {
            setResults(response.results);
            setShowSelectedItems(true);
            setRankMode('regular');
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
         .catch((err) => {
             showError({
                 code: err.response?.status || 500,
                 message: 'API Error',
                 description: 'Failed to fetch data',
                 showRefresh: true,
             });
         })
         .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, showError ]);

    const onRerankMany = useCallback((dataset: string, scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankManyApps(
            transformCriteriaData(criteriaData, criteriaConstraints),
            selectedProductIds,
            pageInfo.page_size,
            1,
            dataset,
        )
        .then(response => {
            setRankMode('multi');
            setOldSelectedProductIds(selectedProductIds);
            setResults(response.results);
            setShowSelectedItems(false);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, selectedProductIds, showError ]);

    const onResetTradeOffs = useCallback(() => {
        if (JSON.stringify(criteriaData.fields) === JSON.stringify(initialCriteriaData.current.fields)) {
            return;
        }

        setCriteriaData({ ...initialCriteriaData.current });
        setResultLoading(true);

        rankApps(
            transformCriteriaData(initialCriteriaData.current, criteriaConstraints),
            pageInfo.page_size,
            pageInfo.current_page,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, initialCriteriaData, pageInfo, showError ]);

    const onSliderChange = (newValue: number, id: string) => {
        setCriteriaData(criteriaData => {
            const fields = criteriaData.fields;
            const changingFieldIdx = fields.findIndex(field => field.id === id);
            return {
                ...criteriaData,
                fields: [
                    ...fields.slice(0, changingFieldIdx),
                    {
                        ...fields[changingFieldIdx],
                        importance: newValue,
                    },
                    ...fields.slice(changingFieldIdx + 1),
                ],
            };
        });
    };

    const onSliderBulkChange = (newValues: {
        [key: string]: number
    }) => {
        setCriteriaData(criteriaData => (
            {
                ...criteriaData,
                fields: criteriaData.fields.map(field => {
                    const newFieldValue = newValues[field.id];

                    if (!newFieldValue) {
                        return {
                            ...field,
                            importance: 0,
                        };
                    }

                    return {
                        ...field,
                        importance: newFieldValue,
                    };
                }),
            }
        ));
    };

    const onTradeoffValueChange = (newValue: number | string, id: string, key?: string) => {
        setCriteriaData(criteriaData => {
            const fields = criteriaData.fields;
            const idx = criteriaData.fields.findIndex(field => field.id === id);
            const newVal = getCriteriaValueByType(fields[idx], newValue, key);

            return {
                ...criteriaData,
                fields: [
                    ...fields.slice(0, idx),
                    {
                        ...fields[idx],
                        val: newVal,
                    },
                    ...fields.slice(idx + 1),
                ],
            };
        });
    };

    const onPageChange = (dataset: string, page: number) => {
        setResultLoading(true);
        if (rankMode === 'regular') {
            rankApps(
                transformCriteriaData(criteriaData, criteriaConstraints),
                pageInfo.page_size,
                page,
                dataset,
            )
            .then(response => {
                setResults(response.results);
                setShowSelectedItems(true);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .finally(() => setResultLoading(false));
        } else {
            rerankManyApps(
                transformCriteriaData(criteriaData, criteriaConstraints),
                oldSelectedProductIds,
                pageInfo.page_size,
                page,
                dataset,
            )
            .then(response => {
                setResults(response.results);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to fetch data',
                    showRefresh: true,
                });
            })
            .finally(() => setResultLoading(false));
        }
    };

    const onActionClick = useCallback((action: AppActions) => {
        switch (action) {
            case 'DECIDE':
                return onCriteriaApply('main');
            case 'IMPORTANCE':
                return console.info('Open Importance');
            case 'SETTINGS':
                return console.info('Open SETTINGS');
            case 'RESET':
                return onResetTradeOffs();
        }
    }, [ onCriteriaApply, onResetTradeOffs ]);

    useEffect(() => {
        setExternalItems(menuItems.externalItems);

        if (!headerMenu) {
            return;
        }

        const headerMenus = [ ...menuItems.additionalItems, ...initialHeaderLinks ];

        setHeaderLinks(headerMenus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ headerMenu, menuItems.additionalItems, menuItems.externalItems ]);

    const isFlowRoute = window.location.pathname.includes('/flows/');
    if (isAuthLoading) {
        return (
            <>
                {!isAuthenticated && (
                    <SessionTokenIframe/>
                )}
                <Loading/>
            </>
        );
    }
    if (error) {
        return (
            <ErrorPage
                code={error.code}
                message={error.message}
                description={error.description}
                showRefresh={error.showRefresh}
                onRefresh={() => {
                    clearError();
                    window.location.reload();
                }}
            />
        );
    }


    return (
        <div className="app">
            <Router>
                {header && !isFlowRoute ? (
                    <Header
                        showDecide={headerMenu && menuItems.decide}
                        showReset={headerMenu && menuItems.reset}
                        headerLinks={headerLinks}
                        externalItems={externalItems}
                        onActionClick={onActionClick}
                        shouldDisplaySteps={false}
                    />
                ) : null}
                <div className="app-content" style={{ minHeight: 'calc(100vh - 122px)' }}>
                    <UnauthorizedBoundary>
                        {isAuthenticated ? (
                            <Routes>
                                <Route path="/apps/:id" element={
                                    <ApplicationV2
                                        initialCriteriaData={initialCriteriaData}
                                        criteriaData={criteriaData}
                                        criteriaConstraints={criteriaConstraints}
                                        setCriteriaConstraints={setCriteriaConstraints}
                                        setCriteriaData={setCriteriaData}
                                        onRerank={onRerank}
                                        onRerankMany={onRerankMany}
                                        onPageChange={onPageChange}
                                        pageInfo={pageInfo}
                                        onTradeoffValueChange={onTradeoffValueChange}
                                        onSliderChange={onSliderChange}
                                        results={results}
                                        resultLoading={resultLoading}
                                        setResultLoading={setResultLoading}
                                        onCriteriaApply={onCriteriaApply}
                                        onResetTradeOffs={onResetTradeOffs}
                                        onSliderBulkChange={onSliderBulkChange}
                                        setPageInfo={setPageInfo}
                                        setResults={setResults}
                                        selectedProductIds={showSelectedItems ? selectedProductIds : []}
                                        setSelectedProductIds={setSelectedProductIdsWrapper}
                                    />
                                }/>
                                <Route path="/terms-of-use" element={<TermsOfUse/>}/>
                                <Route path="/virtual-agent/:id" element={<VirtualAgent/>}/>
                                <Route path="/forms/:formId" element={<Forms/>}/>
                                <Route path="/mashups/:mashupId" element={<Mashups/>}/>
                                <Route path="/cardiology-new" element={<CardiologyEnhancedV2/>}/>
                                <Route path="/create-website/:id" element={<CreateWebsite/>}/>
                                <Route path="/edit-website/:id" element={<WebsiteEditor/>}/>
                                <Route path="/flows/:id" element={<Flows/>}/>
                                <Route path="/" element={<Applications/>}/>
                                <Route path="*" element={<Navigate to="/" replace/>}/>
                            </Routes>
                        ) : (
                            <Loading/>
                        )}
                    </UnauthorizedBoundary>
                </div>
                {footer ? <Footer/> : null}
            </Router>
        </div>
    );
}

export default App;
